import { useMemo, useState } from "react";
import { Trash2, RefreshCw, MessageCircle, ChevronDown, ChevronUp } from "lucide-react";
import { renderMessageText } from "@/lib/rgHtmlRender";

/**
 * TimelineConversation — historique lisible des échanges.
 *   - Voyageur (gauche) et Curieux Refuge (droite) clairement distingués.
 *   - Nom réel de l'expéditeur, date & heure réelles, texte complet, ordre chronologique.
 *   - Provenance Lodgify indiquée discrètement.
 *   - Aperçu des derniers messages + bouton « Voir toute la conversation ».
 *   - Suppression protégée par confirmation (gérée par le parent).
 */

const PREVIEW_COUNT = 3;

function classifyChannel(m) {
  const raw = (m.channel || (m.from_lodgify ? "lodgify" : "") || "").toLowerCase();
  if (raw.includes("airbnb")) return "airbnb";
  if (raw.includes("booking")) return "booking";
  if (raw.includes("whatsapp") || raw === "wa") return "whatsapp";
  if (raw.includes("sms")) return "sms";
  if (raw.includes("couchage")) return "formulaire";
  if (raw.includes("lodgify")) return "lodgify";
  if (raw.includes("direct")) return "direct";
  return "";
}
const CHAN_LABEL = { airbnb: "Airbnb", booking: "Booking", whatsapp: "WhatsApp", sms: "SMS", lodgify: "Lodgify", formulaire: "Formulaire", direct: "Direct" };

function fmtAbsolute(ts) {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return "";
    return `${d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })} · ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return ""; }
}

export default function TimelineConversation({ reservation, onDeleteMessage, onLodgifySync, busy }) {
  const [expanded, setExpanded] = useState(false);
  const messages = reservation?.messages || [];
  const guestName = (reservation?.name || "").trim().split(/\s+/)[0] || "Voyageur";

  const sorted = useMemo(
    () => [...messages].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
    [messages]
  );

  const visible = expanded ? sorted : sorted.slice(-PREVIEW_COUNT);
  const hiddenCount = sorted.length - visible.length;

  const Bubble = ({ m }) => {
    const isHost = m.sender === "host";
    const chan = classifyChannel(m);
    const senderName = isHost ? "Curieux Refuge" : guestName;
    const when = fmtAbsolute(m.timestamp);
    return (
      <div className={`rg-timeline-row ${isHost ? "host" : "guest"}`} data-testid={`rg-bubble-${m.id}`}>
        <div className={`rg-bubble ${isHost ? "host" : "guest"}`}>
          <div className="rg-row" style={{ justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 12, color: isHost ? "var(--rg-primary)" : "var(--rg-text-2)" }}>{senderName}</span>
            <span className="rg-tiny" style={{ color: "var(--rg-text-3)" }}>{when}</span>
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>{renderMessageText(m.text)}</div>
          <div className="rg-row" style={{ justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
            {chan ? <span className="rg-tiny" style={{ color: "var(--rg-text-3)", fontStyle: "italic" }}>via {CHAN_LABEL[chan] || chan}</span> : <span />}
            <button className="rg-bubble-del" onClick={() => onDeleteMessage(m.id)} title="Supprimer ce message"
              data-testid={`rg-bubble-del-${m.id}`} style={{ position: "static", opacity: 0.5 }}>
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="rg-section rg-conversation-hero" data-testid="rg-timeline-hero">
      <div className="rg-row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <MessageCircle size={18} /> Conversation
          {sorted.length > 0 && <span className="rg-tiny" style={{ color: "var(--rg-text-3)", fontWeight: 400 }}>· {sorted.length} message{sorted.length > 1 ? "s" : ""}</span>}
        </h3>
        <div className="rg-row" style={{ gap: 6, flexWrap: "wrap" }}>
          <button className="rg-btn ghost small" onClick={onLodgifySync} disabled={busy}
            data-testid="rg-lodgify-sync-messages" title="Récupérer les messages voyageur depuis Lodgify">
            <RefreshCw size={12} /> Synchroniser Lodgify
          </button>
          <button className="rg-btn ghost small" disabled data-testid="rg-wa-sync-messages"
            title="Synchronisation WhatsApp Business — bientôt disponible" style={{ opacity: 0.55, cursor: "not-allowed" }}>
            <MessageCircle size={12} /> WhatsApp Business <span className="rg-tiny" style={{ marginLeft: 4, fontStyle: "italic" }}>· bientôt</span>
          </button>
        </div>
      </div>

      <div className="rg-timeline" data-testid="rg-timeline">
        {sorted.length === 0 && <div className="rg-timeline-empty">Aucun message pour le moment.</div>}

        {!expanded && hiddenCount > 0 && (
          <button className="rg-btn ghost small" style={{ width: "100%", marginBottom: 8 }}
            onClick={() => setExpanded(true)} data-testid="rg-conv-expand">
            <ChevronDown size={13} /> Voir toute la conversation ({sorted.length})
          </button>
        )}

        {visible.map((m) => <Bubble key={m.id} m={m} />)}

        {expanded && sorted.length > PREVIEW_COUNT && (
          <button className="rg-btn ghost small" style={{ width: "100%", marginTop: 8 }}
            onClick={() => setExpanded(false)} data-testid="rg-conv-collapse">
            <ChevronUp size={13} /> Réduire
          </button>
        )}
      </div>
    </div>
  );
}
